import React, { CSSProperties, KeyboardEvent, useState } from 'react';

// utils
import { isEmpty } from 'lodash';
import { checkValidKey } from '../../util/util';

// styles
import qmStyles from '../../theme/qmStyles';

// components
import Clickable from '../Clickable';

interface SecuredFormInputProps {
  required?: boolean;
  onChange?: () => void | undefined;
  onSubmit?: () => void | undefined;
  innerRef: any;
  placeholder?: string;
  name?: string;
  value: number | string;
  error?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  borderColor?: string;
  errorBorderColor?: string;
  backgroundColor?: string;
  textColor?: string;
  hideBorder?: boolean;
  autocomplete?: string;
  type?: string;
  label?: string;
  pattern: string;
  title: string;
}

// for now we assume there are no duplicate labels in the same form
const makeIdFromLabel = (label: string = '') => label.replace(' ', '_');

const SecuredFormInput: React.FC<SecuredFormInputProps> = ({
  name,
  onChange = undefined,
  onSubmit = undefined,
  backgroundColor = qmStyles.white,
  borderColor = qmStyles.borderColor,
  errorBorderColor = 'red',
  textColor = qmStyles.greyDarkest,
  hideBorder = false,
  error = false,
  disabled = false,
  required = false,
  autocomplete = 'on',
  label = undefined,
  title,
  pattern,
  placeholder,
  style,
  value,
  innerRef,
}) => {
  const [visible, setVisible] = useState<boolean>(false);

  const toggleVisibility = () => {
    setVisible(prev => !prev);
  };

  const onKeyPress = (e: KeyboardEvent) => {
    if (!onSubmit) return;
    if (checkValidKey(e.keyCode)) {
      onSubmit();
    }
  };

  const renderInput = (additionalProps: any = {}) => (
    <div
      style={{
        height: 40,
        borderColor: error ? errorBorderColor : borderColor,
        borderStyle: 'solid',
        borderRadius: qmStyles.borderRadius,
        borderWidth: hideBorder ? 0 : 1,
        backgroundColor,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        ...style,
      }}
    >
      <input
        style={{
          paddingLeft: qmStyles.gutter,
          paddingRight: qmStyles.gutter,
          WebkitAppearance: 'none',
          color: textColor,
          fontSize: 14,
          borderWidth: 0,
        }}
        required={required}
        pattern={pattern}
        title={title}
        ref={innerRef}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        name={name}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        onClick={onSubmit}
        onKeyPress={onKeyPress}
        autoComplete={autocomplete}
        {...additionalProps}
      />
      <Clickable
        onClick={toggleVisibility}
        style={{
          marginLeft: -30,
          cursor: 'pointer',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          padding: qmStyles.quarterGutter,
          backgroundColor: 'transparent',
        }}
      >
        <span
          className={`icon ${visible ? 'qm-icon-eye' : 'qm-icon-eye-disabled'}`}
          style={{
            fontSize: 16,
            color: qmStyles.greyMedium,
            textAlign: 'center',
            backgroundColor: 'transparent',
          }}
        />
      </Clickable>
    </div>
  );

  if (!isEmpty(label)) {
    const forId = makeIdFromLabel(label);
    return (
      <>
        <label
          className="self-start text-14 font-normal color-grey-darkest"
          htmlFor={forId}
        >
          {label}
        </label>
        {renderInput({ id: forId })}
      </>
    );
  }
  return renderInput();
};

export default React.forwardRef((props: SecuredFormInputProps, ref) => (
  <SecuredFormInput {...props} innerRef={ref} />
));
